import { Box, Divider, Grid, Link, Stack, useTheme } from '@mui/material';
import useResponsive from '@/hooks/useResponsive';
import classNames from 'classnames';
import Image from 'next/image';
import * as React from 'react';
import { AppRow, Columns, SocialRow } from './Data';
import {
  AppBox,
  BoldText,
  CompanyBox,
  Contact,
  CopyRight,
  ImageBox,
  ImageBox2,
  ImageBoxApp,
  IntroFooter,
  Scan,
  Scan2,
  Social
} from './Styles';
import Script from 'next/script';
import dynamic from 'next/dynamic';

const Typography = dynamic(() => import('@/components/Typography'));

const Footer: React.FC = () => {
  const theme = useTheme();
  const { matches } = useResponsive();

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'رابکس',
    url: 'https://rabex.ir/',
    telephone: '(021) 22069128',
    email: 'mailto:info@rabex.ir',
    logo: 'https://rabex.ir/wp-content/uploads/2022/05/rabex-home-of-digital-assets-fa.svg',
    description:
      'خرید ارز دیجیتال و فروش ارز دیجیتال با بهترین قیمت در صرافی ارز دیجیتال رابکس به صورت آنلاین ، معتبر ترین سایت خرید و فروش رمز ارز در ایران',
    sameAs: [
      ...SocialRow.map((social, index) => social.link),
      ...AppRow.map((app, index) => app.link)
    ]
  };

  return (
    <Box
      px={{ xs: 5, lg: 35 }}
      sx={{
        background: 'white',
        pt: matches({ xs: 10, lg: 13 })
      }}
    >
      <Script
        strategy="beforeInteractive"
        id="organization-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
      <Box sx={{ flexGrow: 1 }}>
        <Stack
          direction={{ lg: 'row', xs: 'column' }}
          alignItems="center"
          justifyContent="space-between"
          spacing={{ xs: 2, lg: 38 }}
        >
          <Box sx={{ alignItems: 'center' }}>
            <Box sx={{ pr: matches({ xs: 0, lg: 12 }) }}>
              <IntroFooter>
                <Image
                  src="/assets/Rabex.svg"
                  alt="rabex"
                  width={43}
                  height={54}
                />
                <Box
                  sx={{
                    fontSize: '14px',
                    color: theme.palette.textColor.A60
                  }}
                >
                  <BoldText component="span" className={classNames('test')}>
                    رابکس (شرکت دانش بنیان تجارت بین‌الملل رابین)
                  </BoldText>
                  پلتفرم جامع خرید و فروش ارز های دیجیتال در ایران است که
                  می‌توانید در آن با امنیتی بالا و با بهترین قیمت به معامله
                  بپردازید. حفظ دارایی‌های مشتریان اولویت رابکس است.
                </Box>
              </IntroFooter>
            </Box>
          </Box>
          <Box>
            <Social>
              {SocialRow.map((social, index) => (
                <ImageBox key={index}>
                  <a
                    href={social.link}
                    target="_blank"
                    aria-label="social link"
                    style={{ cursor: 'pointer', textDecoration: 'none' }}
                  >
                    <Stack
                      spacing={4}
                      direction="row"
                      justifyContent="center"
                      alignItems="center"
                      color={theme.palette.textColor.A50}
                      sx={{
                        '&:hover': { color: theme.palette.primary.A100 }
                      }}
                    >
                      {social.icon}
                    </Stack>
                  </a>
                </ImageBox>
              ))}
            </Social>
          </Box>
        </Stack>
      </Box>
      <Divider
        orientation="horizontal"
        variant="middle"
        sx={{
          borderStyle: 'solid',
          borderColor: theme.palette.grayBackground.A20,
          mx: 'auto',
          mt: 8,
          mb: 11,
          [`${theme.breakpoints.down('sm')}`]: {
            display: 'none'
          }
        }}
      />

      <Grid
        container
        spacing={4}
        margin={{ xs: 'auto 6px', sm: '0', lg: '0', xl: '0' }}
      >
        {Columns.map((item, columnIndex) => (
          <Grid item lg={2.4} xs={6} sm={6} xl={2.4} key={item.title}>
            <Stack direction={{ lg: 'row', xs: 'column' }}>
              <Stack>
                <Box sx={{ my: 1 }}>
                  <Typography
                    variant="body1"
                    style={{ fontWeight: '600' }}
                    color={theme.palette.textColor.A80}
                    sx={{
                      [`${theme.breakpoints.down('sm')}`]: {
                        display: 'none'
                      }
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    style={{ fontWeight: '600' }}
                    color={theme.palette.textColor.A80}
                    sx={{
                      display: 'none',
                      [`${theme.breakpoints.down('sm')}`]: {
                        display: 'block'
                      }
                    }}
                  >
                    {item.shortTitle}
                  </Typography>
                </Box>
                {item.descriptions.map((description, index) => (
                  <Box sx={{ my: 1 }} key={index}>
                    <a
                      target={columnIndex === 1 ? '_blank' : '_parent'}
                      href={description.link}
                    >
                      <Typography
                        color={theme.palette.textColor.A60}
                        variant="body2"
                        sx={{
                          textDecoration: 'none',
                          cursor: 'pointer',
                          '&:hover': { color: theme.palette.primary.A100 }
                        }}
                      >
                        {description.desc}
                      </Typography>
                    </a>
                  </Box>
                ))}
              </Stack>
            </Stack>
          </Grid>
        ))}
        <Grid item lg={2.4} xs={12} sm={6} xl={2.4}>
          <Box>
            <Stack>
              <Box sx={{ my: 1 }}>
                <Typography
                  variant="body1"
                  style={{ fontWeight: '600' }}
                  color={theme.palette.textColor.A80}
                >
                  راه‌های ارتباطی
                </Typography>
              </Box>
              <Box sx={{ my: 1 }}>
                <Typography color={theme.palette.textColor.A60} variant="body2">
                  ۷ روز هفته، ۲۴ ساعته پاسخگوی شما هستیم.
                </Typography>
              </Box>
              <Contact sx={{ my: 1 }}>
                <Image
                  src="/assets/Email.svg"
                  alt="email"
                  width={17}
                  height={17}
                />
                <a href="mailto:info@rabex.ir">
                  <Typography
                    color={theme.palette.textColor.A60}
                    variant="body2"
                    sx={{
                      textDecoration: 'none',
                      cursor: 'pointer',
                      '&:hover': { color: theme.palette.primary.A100 }
                    }}
                  >
                    info@rabex.ir
                  </Typography>
                </a>
              </Contact>
              <Contact sx={{ my: 1 }}>
                <Image
                  src="/assets/Call.svg"
                  alt="call"
                  width={18}
                  height={18}
                />
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'end'
                  }}
                >
                  <a href="tel:02191001678">
                    <Typography
                      color={theme.palette.textColor.A60}
                      variant="body2"
                      sx={{
                        direction: 'rtl',
                        textAlign: 'end',
                        textDecoration: 'none',
                        cursor: 'pointer',
                        '&:hover': { color: theme.palette.primary.A100 }
                      }}
                    >
                      ۰۲۱ ۹۱۰۰ ۱۶ ۷۸
                    </Typography>
                  </a>
                </Box>
              </Contact>
            </Stack>
          </Box>
        </Grid>
      </Grid>
      <Divider
        orientation="horizontal"
        variant="middle"
        sx={{
          borderStyle: 'solid',
          borderColor: theme.palette.grayBackground.A20,
          margin: '24px auto',
          [`${theme.breakpoints.down('sm')}`]: {
            display: 'none'
          }
        }}
      />
      <Grid container spacing={2} sx={{ mt: 7, mb: 6, alignItems: 'center' }}>
        <Grid item xs={12} lg={6} xl={6} sm={6}>
          <Box sx={{ display: 'flex' }}>
            <AppBox
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '2rem',
                alignItems: 'center',
                [`${theme.breakpoints.down('sm')}`]: {
                  flexDirection: 'column',
                  margin: 'auto'
                }
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  [`${theme.breakpoints.down('sm')}`]: {
                    display: 'none'
                  }
                }}
              >
                <Image
                  src="/assets/qrCode.svg"
                  alt="qrcode"
                  width={84}
                  height={84}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '14px'
                }}
              >
                <Scan>برای دریافت اپلیکیشن، اسکن کنید</Scan>
                <Scan2>برای دریافت اپلیکیشن، از طریق لینک زیر اقدام کنید</Scan2>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '1rem',
                    [`${theme.breakpoints.down('sm')}`]: {
                      margin: 'auto'
                    }
                  }}
                >
                  {AppRow.map((app, index) => (
                    <ImageBoxApp
                      key={index}
                      sx={{ '&:focus-visible': { outline: 'none' } }}
                    >
                      <a
                        href={app.link}
                        aria-label="app link"
                        style={{
                          cursor: 'pointer',
                          textDecoration: 'none',
                          outline: 'none'
                        }}
                      >
                        <Stack
                          spacing={4}
                          direction="row"
                          justifyContent="center"
                          alignItems="center"
                          color={theme.palette.textColor.A60}
                          sx={{
                            '&:hover': { color: theme.palette.primary.A100 },
                            '&:focus-visible': { outline: 'none' },
                            outline: 'none'
                          }}
                        >
                          {app.icon}
                        </Stack>
                      </a>
                    </ImageBoxApp>
                  ))}
                </Box>
              </Box>
            </AppBox>
          </Box>
        </Grid>
        <Grid item xs={12} lg={6} xl={6} sm={6}>
          <CompanyBox
            sx={{
              [`${theme.breakpoints.down('sm')}`]: {
                display: 'none'
              }
            }}
          >
            <ImageBox2>
              <Image
                src="/assets/Danesh.svg"
                alt="danesh"
                width={78}
                height={99}
              />
            </ImageBox2>
            <ImageBox2>
              <Image
                src="/assets/fintech.svg"
                alt="fintech"
                width={79}
                height={93}
              />
            </ImageBox2>
          </CompanyBox>

          <CompanyBox
            sx={{
              display: 'none',
              [`${theme.breakpoints.down('sm')}`]: {
                display: 'flex'
              }
            }}
          >
            <ImageBox2>
              <Image
                src="/assets/Big-Danesh.svg"
                alt="danesh"
                width={90}
                height={115}
              />
            </ImageBox2>
            <ImageBox2>
              <Image
                src="/assets/fintech.svg"
                alt="fintech"
                width={79}
                height={93}
              />
            </ImageBox2>
          </CompanyBox>
        </Grid>
      </Grid>
      <Divider
        orientation="horizontal"
        variant="middle"
        sx={{
          borderStyle: 'solid',
          borderColor: theme.palette.grayBackground.A20,
          margin: '24px auto',
          [`${theme.breakpoints.down('sm')}`]: {
            display: 'none'
          }
        }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', pb: 6 }}>
        <CopyRight>
          <Typography variant="body3" sx={{ display: 'inline' }}>
            تمامی حقوق مادی و معنوی این سایت متعلق به رابکس
          </Typography>
          <Typography weight="bold" variant="body3" component="span">
            (شرکت دانش‌بنیان تجارت بین‌الملل رابین){' '}
          </Typography>
          است.
        </CopyRight>
      </Box>
    </Box>
  );
};
export default Footer;
